import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ChevronDown, X } from "lucide-react";

export default function ConferenceFilters({
  selectedFilter,
  onFilterChange,
  searchQuery,
  onSearchChange,
  customFilters = null,
  placeholder = "Search conferences and trade missions...",
}) {
  const [showAllFilters, setShowAllFilters] = useState(false);

  const baseFilters = [
    { key: "all", label: "All" },
    { key: "conferences", label: "Conferences" },
    { key: "trade_missions", label: "Trade Missions" },
  ];

  const filters = customFilters
    ? customFilters.map((key) => ({
        key,
        label:
          key === "all"
            ? "All"
            : key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      }))
    : baseFilters;

  const MAX_VISIBLE_FILTERS = 5;
  const hasManyFilters = filters.length > MAX_VISIBLE_FILTERS;
  const visibleFilters =
    hasManyFilters && !showAllFilters
      ? filters.slice(0, MAX_VISIBLE_FILTERS - 1)
      : filters;
  const hiddenFilters =
    hasManyFilters && !showAllFilters
      ? filters.slice(MAX_VISIBLE_FILTERS - 1)
      : [];

  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <div className="relative flex-1 max-w-md">
        <span className="absolute left-3 top-1/2 text-gray-400 -translate-y-1/2">
          <Search size={18} />
        </span>
        <input
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="py-2.5 pr-4 pl-10 w-full text-sm bg-white rounded-lg border outline-none border-emerald-300/60 focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 placeholder:text-gray-400 transition-all"
        />
      </div>
      <div className="flex flex-wrap gap-2 items-center md:gap-3">
        {visibleFilters.map((filter) => {
          const isActive = selectedFilter === filter.key;
          return (
            <motion.button
              key={filter.key}
              onClick={() => onFilterChange(filter.key)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className={`px-5 py-2.5 text-sm font-medium rounded-full transition-all shadow-sm whitespace-nowrap ${
                isActive
                  ? "text-white bg-gradient-to-r from-emerald-500 to-green-600"
                  : "text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              {filter.label}
            </motion.button>
          );
        })}
        {hasManyFilters && (
          <motion.button
            onClick={() => setShowAllFilters(!showAllFilters)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex gap-1.5 items-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-white rounded-full border border-gray-300 hover:bg-gray-50 hover:border-emerald-400 transition-all shadow-sm"
          >
            {showAllFilters ? (
              <>
                <X size={16} />
                <span>Show Less</span>
              </>
            ) : (
              <>
                <span>+{hiddenFilters.length} More</span>
                <ChevronDown size={16} />
              </>
            )}
          </motion.button>
        )}
      </div>
      <AnimatePresence>
        {showAllFilters && hasManyFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden w-full"
          >
            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
              {hiddenFilters.map((filter) => {
                const isActive = selectedFilter === filter.key;
                return (
                  <motion.button
                    key={filter.key}
                    onClick={() => {
                      onFilterChange(filter.key);
                      setShowAllFilters(false);
                    }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className={`px-5 py-2.5 text-sm font-medium rounded-full transition-all shadow-sm whitespace-nowrap ${
                      isActive
                        ? "text-white bg-gradient-to-r from-emerald-500 to-green-600"
                        : "text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200"
                    }`}
                  >
                    {filter.label}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
